/**
 * =========================================================================
 * 🛡️ ARCHITECTURAL PRESERVATION GUARD — DO NOT COMPROMISE OR MODIFY CONTRACT
 * -------------------------------------------------------------------------
 * MANDATORY REQUIREMENT: Universal Header (<SiteNav>) & Footer (<SiteFooter>) Parity.
 * This component is the SINGLE SOURCE OF TRUTH for the site navigation header
 * across ALL pages: LandingPage, ArticlesPage, ArticleView, ProductPage, ShopPage,
 * CartPage, PageView, PromotionalPage, SearchPage, NotFoundPage, and EcommercePaywall.
 * 
 * CORE RULES:
 * 1. ZERO REGRESSIONS: Never remove, break, or diverge header styling, dropdowns,
 *    mobile menu drawers, currency/language selectors, cart badges, or search widgets.
 * 2. CONTRACT INTEGRITY: Always maintain support for:
 *    - config, settings, socials, modules
 *    - onSetConfig, onSaveConfig, getFontStyles, getShadow, getSideBorder
 *    - anchorMode ('link' | 'scroll'), onAnchorClick, titleEditable
 *    - isEditing, isAuthenticated, cartCount
 * 3. DEFENSIVE RESOLUTION: Always preserve internal fallbacks to config.settings,
 *    config.socials, and config.modules.
 * =========================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getNestedNavLinks } from '../utils/helpers.js';
import { addUnit, resolveMediaUrl, normalizeAppUrl, handleAppUrlClick, getAppBase, getBackground, stripHtml, isFileUrl, isExternalOrFileUrl } from '../utils/styleHelpers.js';
import { getSocialIcon as defaultGetSocialIcon } from '../utils/socialIcons.jsx';
import { useCart } from '../ecommerce/CartState';
import { isEcommerceUnlocked } from '../ecommerce/branding';
import { InlineSearchBar } from './SearchTool';
import { CustomerOrdersModal } from '../ecommerce/CustomerOrdersModal';
import { CurrencySelector } from '../ecommerce/CurrencySelector';
import { LanguageSelector, isI18nEnabled } from '../i18n/index.js';


const SiteNav = ({
    config = {},
    settings = config.settings || {},
    socials = (config.socials && config.socials.length) ? config.socials : [],
    modules = config.modules || {},
    onSetConfig = config.onSetConfig,
    onSaveConfig = config.onSaveConfig,
    getFontStyles = config.getFontStyles,
    getShadow = config.getShadow,
    getSideBorder = config.getSideBorder,
    getSocialIcon = defaultGetSocialIcon,
    titleEditable,       // pass <Editable> element for site title, or null for plain text
    anchorMode = 'scroll',
    onAnchorClick,       // (e, url) => void
    isEditing = config.isEditing || false,
    isAuthenticated = config.isAuthenticated || false,
    forceMobile = false,
    forceOpen = null,
    onToggleMenu,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [internalMenuOpen, setInternalMenuOpen] = useState(false);
    const [mobileExpandedMenus, setMobileExpandedMenus] = useState({});
    const isMenuOpen = forceOpen !== null && forceOpen !== undefined ? forceOpen : internalMenuOpen;
    const setIsMenuOpen = (val) => {
        const nextVal = typeof val === 'function' ? val(isMenuOpen) : val;
        setInternalMenuOpen(nextVal);
        if (onToggleMenu) onToggleMenu(nextVal);
    };
    const toggleMobileSubmenu = (index, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setMobileExpandedMenus(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };
    const [isShopHovered, setIsShopHovered] = useState(false);
    const [isCartHovered, setIsCartHovered] = useState(false);
    const [isOrdersHovered, setIsOrdersHovered] = useState(false);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);

    useEffect(() => {
        if (location?.search && (location.search.includes('orders=true') || location.search.includes('my-orders=true'))) {
            setIsOrdersModalOpen(true);
        }
    }, [location?.search]);


    const updateSetting = (key, value) => {
        const nextSettings = {
            ...(config.settings || settings),
            [key]: value
        };
        const nextConfig = {
            ...config,
            settings: nextSettings
        };

        if (onSetConfig) {
            onSetConfig(prev => ({
                ...prev,
                settings: {
                    ...(prev?.settings || {}),
                    [key]: value
                }
            }));
        } else if (onSaveConfig) {
            onSaveConfig(nextConfig);
        }
    };

    // Cart Context & Module Status
    const cartContext = useCart ? useCart() : null;
    const cartCount = cartContext?.cartCount || 0;
    const isEcommerceEnabled = isEcommerceUnlocked({ modules });

    // ── Hide-on-scroll & Dynamic Scroll-Responsive Header logic ────────────────
    const hideOnScroll = !!settings.navHideOnScroll;
    const easingPreset = settings.navScrollEasing || '0.35s ease';
    const enableScrollStyles = !!settings.headerEnableScrollStyles;
    const scrollThreshold = parseInt(settings.headerScrollThreshold) || 50;

    const [navHidden, setNavHidden] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const handleScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(() => {
                    const currentY = window.scrollY;

                    if (enableScrollStyles) {
                        setIsScrolled(currentY > scrollThreshold);
                    } else {
                        setIsScrolled(false);
                    }

                    if (hideOnScroll) {
                        if (currentY <= 60) {
                            setNavHidden(false);
                        } else {
                            const diff = currentY - lastScrollY.current;
                            if (diff > 8) {
                                setNavHidden(true);
                            } else if (diff < -8) {
                                setNavHidden(false);
                            }
                        }
                    } else {
                        setNavHidden(false);
                    }
                    lastScrollY.current = Math.max(0, currentY);
                    ticking.current = false;
                });
                ticking.current = true;
            }
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hideOnScroll, enableScrollStyles, scrollThreshold]);

    const renderIcon = (platform, size = 16) => {
        if (getSocialIcon) return getSocialIcon(platform, size);
        return defaultGetSocialIcon(platform, size);
    };

    const showHeaderSocials = !!settings.showSocialsInHeader || settings.socialsPlacement === 'header' || !!settings.headerShowSocials;
    const showMobileSocials = showHeaderSocials || settings.showSocialsInMobileMenu !== false;

    const [isDemoActive, setIsDemoActive] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem('omnivaso_demo_mode') === 'true' || window.location.search.includes('demo=true');
    });

    useEffect(() => {
        const updateDemoState = () => {
            const searchHasDemo = location?.search ? (new URLSearchParams(location.search).get('demo') === 'true' || location.search.includes('demo=')) : false;
            const active = typeof window !== 'undefined' && (localStorage.getItem('omnivaso_demo_mode') === 'true' || window.location.search.includes('demo=true') || searchHasDemo);
            setIsDemoActive(active);
            if (typeof document !== 'undefined') {
                document.body.style.paddingTop = active ? '46px' : '';
            }
        };
        updateDemoState();
        window.addEventListener('storage', updateDemoState);
        const timer = setInterval(updateDemoState, 300);
        return () => {
            window.removeEventListener('storage', updateDemoState);
            clearInterval(timer);
        };
    }, [location?.search]);

    const resolveShadow = (prefix) => {
        if (typeof getShadow === 'function') {
            const res = getShadow(prefix);
            if (typeof res === 'string' && res !== '') return res;
            const res2 = getShadow(settings, prefix);
            if (typeof res2 === 'string' && res2 !== '') return res2;
        }
        const preset = settings[`${prefix}ShadowPreset`];
        const custom = settings[`${prefix}ShadowCustom`] || settings[`${prefix}CustomShadow`];
        if (preset === 'none') return 'none';
        if (preset === 'subtle') return '0 2px 8px rgba(0,0,0,0.12)';
        if (preset === 'medium') return '0 4px 16px rgba(0,0,0,0.2)';
        if (preset === 'hard') return '0 8px 28px rgba(0,0,0,0.35)';
        if (preset === 'softGlow') return '0 0 15px rgba(59,130,246,0.4)';
        if (preset === 'darkGlow') return '0 0 20px rgba(0,0,0,0.7)';
        if (preset === 'custom' && custom) return custom;
        if (custom) return custom;

        const color = settings[`${prefix}ShadowColor`];
        if (!color || color === 'transparent') return 'none';
        const x = addUnit(settings[`${prefix}ShadowX`] || '0px');
        const y = addUnit(settings[`${prefix}ShadowY`] || '0px');
        const blur = addUnit(settings[`${prefix}ShadowBlur`] || '0px');
        const spread = addUnit(settings[`${prefix}ShadowSpread`] || '0px');
        const inset = settings[`${prefix}ShadowInset`] ? 'inset ' : '';
        if (x === '0px' && y === '0px' && blur === '0px' && spread === '0px') return 'none';
        return `${inset}${x} ${y} ${blur} ${spread !== '0px' ? spread + ' ' : ''}${color}`;
    };

    const resolveSideBorder = (prefix) => {
        if (typeof getSideBorder === 'function') {
            const res = getSideBorder(prefix);
            if (typeof res === 'string' && res !== '') return res;
            const res2 = getSideBorder(settings, prefix);
            if (typeof res2 === 'string' && res2 !== '') return res2;
        }
        const width = addUnit(settings[`${prefix}Width`] || '0px');
        const color = settings[`${prefix}Color`] || 'transparent';
        const style = settings[`${prefix}Style`] || 'solid';
        if (width === '0px' || color === 'transparent' || style === 'none') return 'none';
        return `${width} ${style} ${color}`;
    };

    const useScrolledState = isScrolled && enableScrollStyles;

    // Header Background resolution
    const activeHeaderBg = useScrolledState && settings.scrolledHeaderBgColor
        ? settings.scrolledHeaderBgColor
        : ((forceMobile && settings.mobileHeaderBg)
            ? settings.mobileHeaderBg
            : (settings.headerBgColor || settings.secondaryColor || '#1e293b'));

    // Nav Link / Text color resolution
    const activeNavLinkColor = useScrolledState && settings.scrolledNavLinkColor
        ? settings.scrolledNavLinkColor
        : (settings.navLinkColor || 'white');

    // Backdrop blur resolution
    const activeBackdropBlur = useScrolledState && settings.scrolledHeaderBackdropBlur !== undefined
        ? settings.scrolledHeaderBackdropBlur
        : settings.headerBackdropBlur;

    // Header vertical padding resolution
    const activePaddingTopBottom = useScrolledState && settings.scrolledHeaderPaddingTopBottom !== undefined
        ? settings.scrolledHeaderPaddingTopBottom
        : (settings.headerPaddingTopBottom || '20px');

    const basePaddingTop = (forceMobile && settings.mobileHeaderPaddingTopBottom !== undefined)
        ? addUnit(settings.mobileHeaderPaddingTopBottom)
        : addUnit(activePaddingTopBottom);
    const basePaddingBottom = (forceMobile && settings.mobileHeaderPaddingTopBottom !== undefined)
        ? addUnit(settings.mobileHeaderPaddingTopBottom)
        : addUnit(activePaddingTopBottom);

    // Header shadow resolution
    let activeShadow = resolveShadow('header');
    if (useScrolledState) {
        const scrolledShadow = resolveShadow('scrolledHeader');
        if (scrolledShadow && scrolledShadow !== 'none') {
            activeShadow = scrolledShadow;
        } else if (settings.scrolledHeaderShadowPreset === 'none') {
            activeShadow = 'none';
        }
    }

    const navStyle = {
        paddingTop: `calc(${basePaddingTop} + env(safe-area-inset-top, 0px))`,
        paddingBottom: basePaddingBottom,
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        background: activeHeaderBg,
        color: activeNavLinkColor,
        position: forceMobile ? 'absolute' : 'fixed',
        left: 0,
        right: 0,
        top: (typeof window !== 'undefined' && localStorage.getItem('omnivaso_demo_mode') === 'true' && !forceMobile) ? 'var(--demo-bar-height, 0px)' : 0,
        zIndex: 1000,
        boxShadow: activeShadow,
        borderBottom: resolveSideBorder(useScrolledState && settings.scrolledHeaderBorderBottomColor ? 'scrolledHeaderBorderBottom' : 'headerBorderBottom'),
        boxSizing: 'border-box',
        margin: forceMobile ? '0px' : addUnit(settings.headerMargin || '0px'),
        ...(activeBackdropBlur && activeBackdropBlur !== '0px' && activeBackdropBlur !== '0' && activeBackdropBlur !== 'none' ? {
            backdropFilter: `blur(${addUnit(activeBackdropBlur)})`,
            WebkitBackdropFilter: `blur(${addUnit(activeBackdropBlur)})`
        } : {}),
        // Hide-on-scroll: slide upward when navHidden, restore on scroll-up
        transform: (!forceMobile && hideOnScroll && navHidden) ? 'translateY(-110%)' : 'translateY(0)',
        transition: (!forceMobile && hideOnScroll)
            ? `transform ${easingPreset}, background 0.3s ease, color 0.3s ease, padding 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease`
            : 'background 0.3s ease, color 0.3s ease, padding 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease, top 0.2s ease',
        willChange: (!forceMobile && (hideOnScroll || enableScrollStyles)) ? 'transform, background, padding, box-shadow' : 'auto'
    };

    const linkStyle = {
        color: activeNavLinkColor,
        textDecoration: 'none',
        fontFamily: settings.navFontFamily || (getFontStyles ? getFontStyles('body').fontFamily : 'inherit'),
        fontSize: addUnit(settings.navFontSize || '0.95rem'),
        fontWeight: settings.navFontWeight || 'normal',
        letterSpacing: addUnit(settings.navLetterSpacing || 'normal'),
        textTransform: settings.navTextTransform || 'none',
        cursor: 'pointer',
        padding: addUnit(settings.navLinkPadding || '0px'),
        transition: 'color 0.3s ease',
        pointerEvents: 'auto'
    };

    const childLinkStyle = {
        color: activeNavLinkColor,
        textDecoration: 'none',
        fontFamily: settings.navFontFamily || (getFontStyles ? getFontStyles('body').fontFamily : 'inherit'),
        fontSize: addUnit(settings.navFontSize || '0.9rem'),
        fontWeight: settings.navFontWeight || 'normal',
        letterSpacing: addUnit(settings.navLetterSpacing || 'normal'),
        textTransform: settings.navTextTransform || 'none',
        cursor: 'pointer',
        padding: addUnit(settings.navLinkPadding || '0px'),
        transition: 'color 0.3s ease',
        pointerEvents: 'auto'
    };

    const _getBackground = getBackground(settings);
    let mobileMenuBg = _getBackground('mobileMenu', settings.headerBgColor || settings.secondaryColor || '#1e293b');
    if (settings.mobileMenuBgMode === 'glass') {
        mobileMenuBg = settings.mobileMenuBg || 'rgba(15, 23, 42, 0.88)';
    } else if (settings.mobileMenuBgMode === 'gradient') {
        mobileMenuBg = settings.mobileMenuBg || 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)';
    } else if (settings.mobileMenuBg) {
        mobileMenuBg = settings.mobileMenuBg;
    }

    const mobileMenuTextColor = settings.mobileMenuTextColor || settings.navLinkColor || 'white';
    const hamburgerBarColor = settings.mobileHamburgerColor || settings.navLinkColor || 'white';
    const dividerStyle = settings.mobileMenuDividerStyle || 'solid';
    const dividerColor = settings.mobileMenuDividerColor || 'rgba(255,255,255,0.1)';
    const dividerBorder = (dividerStyle === 'none' || dividerColor === 'transparent') ? 'none' : `1px ${dividerStyle} ${dividerColor}`;

    const layoutMode = settings.mobileMenuLayoutMode || (['glass-sheet', 'slide-drawer', 'bottom-sheet', 'fullscreen-overlay', 'card-grid', 'island-float', 'curtain-drop', 'minimalist-clean'].includes(settings.mobileMenuPosition) ? settings.mobileMenuPosition : 'glass-sheet');
    const isGridMode = layoutMode === 'card-grid';
    const isBottomSheet = layoutMode === 'bottom-sheet';
    const isGlassSheet = layoutMode === 'glass-sheet' || layoutMode === 'glass';
    const isIslandFloat = layoutMode === 'island-float';
    const isCurtainDrop = layoutMode === 'curtain-drop';
    const isMinimalist = layoutMode === 'minimalist-clean';
    const isFullscreen = layoutMode === 'fullscreen-overlay' || layoutMode === 'fullscreen' || layoutMode === 'fade';

    // Box Shadow Preset Calculation
    const boxShadowPreset = settings.mobileMenuBoxShadowPreset || 'glass-glow';
    let computedShadow = '0 20px 40px rgba(0,0,0,0.5)';
    if (boxShadowPreset === 'glass-glow') computedShadow = '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 20px rgba(56, 189, 248, 0.15)';
    else if (boxShadowPreset === 'neon-aura') computedShadow = '0 0 30px rgba(168, 85, 247, 0.35), 0 10px 40px rgba(0,0,0,0.6)';
    else if (boxShadowPreset === 'soft-elevation') computedShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.3)';
    else if (boxShadowPreset === 'deep-night') computedShadow = '0 30px 60px -12px rgba(0, 0, 0, 0.75)';
    else if (boxShadowPreset === 'clean-outline') computedShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
    else if (boxShadowPreset === 'none') computedShadow = 'none';
    if (settings.mobileMenuCustomBoxShadow) computedShadow = settings.mobileMenuCustomBoxShadow;

    const customBorder = settings.mobileMenuBorder;
    const customRadius = settings.mobileMenuRadius;
    const transitionDuration = settings.mobileMenuTransitionDuration || '0.35s';
    const transitionEasing = settings.mobileMenuEasing || 'cubic-bezier(0.16, 1, 0.3, 1)';

    const mobileLinkStyle = {
        color: mobileMenuTextColor,
        textDecoration: 'none',
        fontFamily: settings.navFontFamily || (getFontStyles ? getFontStyles('body').fontFamily : 'inherit'),
        fontSize: addUnit(settings.mobileNavFontSize || (isMinimalist ? '1.15rem' : '0.95rem')),
        fontWeight: settings.mobileNavFontWeight || (isMinimalist ? '600' : '500'),
        letterSpacing: addUnit(settings.mobileNavLetterSpacing || (isMinimalist ? '0.02em' : '0.01em')),
        textTransform: settings.mobileNavTextTransform || 'none',
        textAlign: settings.mobileMenuTextAlign || (isFullscreen ? 'center' : 'left'),
        cursor: 'pointer',
        borderBottom: (isGridMode || isIslandFloat) ? 'none' : dividerBorder,
        paddingBottom: (isGridMode || isIslandFloat) ? '0px' : addUnit(settings.mobileLinkPaddingBottom || '10px'),
        transition: 'all 0.2s ease',
        ...(isGridMode ? {
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.14)',
            borderRadius: '12px',
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        } : isIslandFloat ? {
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        } : {})
    };

    const mobileChildLinkStyle = {
        color: mobileMenuTextColor,
        opacity: 0.85,
        textDecoration: 'none',
        fontFamily: settings.navFontFamily || (getFontStyles ? getFontStyles('body').fontFamily : 'inherit'),
        fontSize: addUnit(settings.mobileNavFontSize ? `calc(${addUnit(settings.mobileNavFontSize)} * 0.9)` : '0.88rem'),
        fontWeight: settings.mobileNavFontWeight || 'normal',
        letterSpacing: addUnit(settings.mobileNavLetterSpacing || 'normal'),
        textTransform: settings.mobileNavTextTransform || 'none',
        textAlign: settings.mobileMenuTextAlign || (isFullscreen ? 'center' : 'left'),
        cursor: 'pointer'
    };

    const menuPosition = settings.mobileMenuPosition || 'right';
    const menuWidth = settings.mobileMenuWidth || '320px';

    let mobileDrawerStyle = {
        background: mobileMenuBg,
        position: forceMobile ? 'absolute' : 'fixed',
        zIndex: 2000,
        boxSizing: 'border-box',
        transition: `all ${transitionDuration} ${transitionEasing}`,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        ...(settings.mobileMenuBgMode !== 'solid' ? {
            backdropFilter: `blur(${addUnit(settings.mobileMenuBackdropBlur || '16px')})`,
            WebkitBackdropFilter: `blur(${addUnit(settings.mobileMenuBackdropBlur || '16px')})`
        } : {})
    };

    if (isBottomSheet) {
        // Preset: Bottom Sheet Drawer
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: forceMobile ? '85%' : '85dvh',
            borderRadius: customRadius || '28px 28px 0 0',
            padding: addUnit(settings.mobileMenuPadding || '16px 20px 28px 20px'),
            gap: '14px',
            borderTop: customBorder || '1px solid rgba(255, 255, 255, 0.18)',
            boxShadow: computedShadow,
            transform: isMenuOpen ? 'translateY(0)' : 'translateY(100%)',
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    } else if (isFullscreen) {
        // Preset: Minimal Fullscreen Overlay
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: forceMobile ? '100%' : '100vw',
            height: forceMobile ? '100%' : '100dvh',
            padding: addUnit(settings.mobileMenuPadding || '70px 24px 30px 24px'),
            gap: '20px',
            justifyContent: 'center',
            alignItems: settings.mobileMenuTextAlign === 'center' ? 'center' : 'flex-start',
            transform: isMenuOpen ? 'scale(1)' : 'scale(1.04)',
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    } else if (isIslandFloat) {
        // Preset: Dynamic Island Capsule
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            top: forceMobile ? '58px' : '72px',
            right: '16px',
            left: '16px',
            maxHeight: forceMobile ? 'calc(100% - 76px)' : 'calc(100dvh - 90px)',
            borderRadius: customRadius || '28px',
            padding: addUnit(settings.mobileMenuPadding || '20px 18px 24px 18px'),
            gap: '12px',
            border: customBorder || '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: computedShadow,
            transform: isMenuOpen ? 'translateY(0) scale(1)' : 'translateY(-14px) scale(0.94)',
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    } else if (isCurtainDrop) {
        // Preset: Full Width Top-Down Curtain Dropdown
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            top: forceMobile ? '50px' : '60px',
            left: 0,
            right: 0,
            maxHeight: forceMobile ? 'calc(100% - 50px)' : 'calc(100dvh - 60px)',
            borderRadius: customRadius || '0 0 24px 24px',
            padding: addUnit(settings.mobileMenuPadding || '24px 22px 30px 22px'),
            gap: '16px',
            borderBottom: customBorder || '1px solid rgba(255, 255, 255, 0.16)',
            boxShadow: computedShadow,
            transform: isMenuOpen ? 'translateY(0)' : 'translateY(-100%)',
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    } else if (isGlassSheet || isGridMode || isMinimalist) {
        // Preset: Floating Glass Sheet Capsule / Split Card Grid / Minimalist Clean
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            top: forceMobile ? '52px' : '64px',
            right: '12px',
            left: '12px',
            maxHeight: forceMobile ? 'calc(100% - 64px)' : 'calc(100dvh - 80px)',
            borderRadius: customRadius || (isMinimalist ? '16px' : '24px'),
            padding: addUnit(settings.mobileMenuPadding || (isMinimalist ? '28px 24px' : '24px 20px')),
            gap: isMinimalist ? '18px' : '16px',
            border: customBorder || '1px solid rgba(255, 255, 255, 0.16)',
            boxShadow: computedShadow,
            transform: isMenuOpen ? 'translateY(0) scale(1)' : 'translateY(-12px) scale(0.96)',
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    } else {
        // Preset: Classic Side Panel Drawer ('slide-drawer')
        mobileDrawerStyle = {
            ...mobileDrawerStyle,
            top: 0,
            bottom: 0,
            right: menuPosition === 'left' ? 'auto' : 0,
            left: menuPosition === 'left' ? 0 : 'auto',
            width: forceMobile ? '82%' : menuWidth,
            maxWidth: forceMobile ? '85%' : '85vw',
            padding: addUnit(settings.mobileMenuPadding || '70px 24px 30px 24px'),
            gap: '16px',
            borderLeft: menuPosition === 'left' ? 'none' : (customBorder || '1px solid rgba(255, 255, 255, 0.12)'),
            borderRight: menuPosition === 'left' ? (customBorder || '1px solid rgba(255, 255, 255, 0.12)') : 'none',
            borderRadius: customRadius || '0px',
            boxShadow: computedShadow,
            transform: isMenuOpen ? 'translateX(0)' : (menuPosition === 'left' ? 'translateX(-100%)' : 'translateX(100%)'),
            opacity: isMenuOpen ? 1 : 0,
            pointerEvents: isMenuOpen ? 'auto' : 'none'
        };
    }

    const brandStyle = {
        color: settings.brandColor || 'white',
        textDecoration: settings.brandTextDecoration || 'none',
        fontWeight: settings.brandFontWeight || 'bold',
        fontSize: addUnit(settings.brandFontSize || '1.2rem'),
        fontFamily: settings.brandFontFamily || (getFontStyles ? getFontStyles('h2').fontFamily : 'inherit'),
        fontStyle: settings.brandFontStyle || 'normal',
        letterSpacing: addUnit(settings.brandLetterSpacing || 'normal'),
        textTransform: settings.brandTextTransform || 'none',
        lineHeight: settings.brandLineHeight || 'normal',
        textShadow: settings.brandTextShadow || 'none',
        display: 'flex',
        alignItems: 'center',
        padding: settings.brandPadding ? addUnit(settings.brandPadding) : (settings.brandPaddingTop || settings.brandPaddingRight || settings.brandPaddingBottom || settings.brandPaddingLeft ? `${addUnit(settings.brandPaddingTop || 0)} ${addUnit(settings.brandPaddingRight || 0)} ${addUnit(settings.brandPaddingBottom || 0)} ${addUnit(settings.brandPaddingLeft || 0)}` : undefined),
        margin: settings.brandMargin ? addUnit(settings.brandMargin) : (settings.brandMarginTop || settings.brandMarginRight || settings.brandMarginBottom || settings.brandMarginLeft ? `${addUnit(settings.brandMarginTop || 0)} ${addUnit(settings.brandMarginRight || 0)} ${addUnit(settings.brandMarginBottom || 0)} ${addUnit(settings.brandMarginLeft || 0)}` : '0px'),
        ...(settings.brandGradientEnabled ? {
            backgroundImage: settings.brandGradient || `linear-gradient(${settings.brandGradientAngle || '135deg'}, ${settings.brandGradientStart || '#3b82f6'}, ${settings.brandGradientEnd || '#ec4899'})`,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent'
        } : {})
    };

    /**
     * Resolve a nav item link based on anchorMode.
     * Returns a React element (<a> or <Link>).
     */
    const renderLink = (url, text, key, style, onClick) => {
        const isFile = isFileUrl(url);
        const normalized = isFile ? resolveMediaUrl(url) : normalizeAppUrl(url);

        const displayText = typeof text === 'string' ? stripHtml(text) : text;

        if (!isFile && url && (url.includes('#') || normalized.startsWith('#'))) {
            return (
                <a
                    key={key}
                    href={normalized}
                    style={style}
                    onClick={(e) => {
                        if (isEditing) {
                            e.preventDefault();
                            return;
                        }
                        if (onClick) onClick(e);
                        const handled = handleAppUrlClick(e, url, isEditing);
                        if (!handled) {
                            if (onAnchorClick) {
                                onAnchorClick(e, normalized);
                            } else {
                                e.preventDefault();
                                const hashIndex = url.indexOf('#');
                                const hashPart = hashIndex !== -1 ? url.substring(hashIndex + 1).replace(/^#/, '') : '';
                                if (hashPart) {
                                    navigate('/', { state: { targetId: hashPart } });
                                } else {
                                    navigate('/');
                                }
                            }
                        }
                    }}
                >
                    {displayText}
                </a>
            );
        }

        const isExternal = isFile || isExternalOrFileUrl(url) || isExternalOrFileUrl(normalized);

        if (!isExternal) {
            return (
                <Link
                    key={key}
                    to={normalized}
                    style={style}
                    onClick={(e) => {
                        if (isEditing) {
                            e.preventDefault();
                            return;
                        }
                        if (onClick) onClick(e);
                    }}
                >
                    {displayText}
                </Link>
            );
        }

        // External or file link
        return (
            <a
                key={key}
                href={normalized}
                target="_blank"
                rel="noopener noreferrer"
                style={style}
                onClick={(e) => {
                    if (isEditing) {
                        e.preventDefault();
                        return;
                    }
                    if (onClick) onClick(e);
                }}
            >
                {displayText}
            </a>
        );
    };

    const nestedLinks = getNestedNavLinks(settings.navLinks);
    const layout = settings.headerLayout || 'left';
    const navAlignment = settings.navAlignment || (layout === 'center' ? 'center' : (layout === 'right' ? 'flex-start' : (layout === 'logo-left-nav-center' ? 'center' : 'flex-end')));
    const actionsGap = addUnit(settings.headerActionsGap || '10px');
    const navLinkGap = addUnit(settings.navLinkGap || '20px');
    const mobileLogoCenter = settings.mobileLogoPosition === 'center';
    const isNavFloating = Boolean(settings.navFloatOverBrand);

    // ── Helper Sub-Renderers ──────────────────────────────────────────────────
    const [logoError, setLogoError] = useState(false);
    const [altLogoSrc, setAltLogoSrc] = useState(null);

    const renderBrandNode = () => {
        if (titleEditable) {
            return titleEditable;
        }

        const brandTarget = normalizeAppUrl(settings.brandLink || '/');
        const isExternal = /^https?:\/\//i.test(brandTarget);
        const rawLogo = settings.siteLogo || settings.logoUrl || settings.logo || settings.brandLogo || settings.brandImage;
        const resolvedLogo = rawLogo ? resolveMediaUrl(rawLogo) : null;
        const currentLogoSrc = altLogoSrc || resolvedLogo;
        const hasLogo = Boolean(currentLogoSrc) && !logoError;
        const showTitle = !hasLogo || Boolean(settings.showTitleWithLogo);
        const logoAltText = settings.siteAlt || settings.siteLogoAlt || stripHtml(settings.siteTitle) || "Logo";

        const handleLogoError = () => {
            if (!altLogoSrc && rawLogo && typeof rawLogo === 'string' && rawLogo.includes('/sites/')) {
                const filename = rawLogo.split('/').pop();
                setAltLogoSrc('/uploads/' + filename);
            } else {
                setLogoError(true);
            }
        };

        const logoImg = hasLogo ? (
            <img
                src={currentLogoSrc}
                onError={handleLogoError}
                alt={logoAltText}
                style={{
                    maxHeight: addUnit(settings.siteLogoHeight || '32px'),
                    width: addUnit(settings.siteLogoWidth || 'auto'),
                    display: 'block',
                    objectFit: settings.siteLogoObjectFit || 'contain',
                    borderRadius: addUnit(settings.siteLogoBorderRadius || '0px'),
                    margin: addUnit(settings.siteLogoMargin || settings.brandMargin || '0px'),
                    padding: addUnit(settings.siteLogoPadding || '0px')
                }}
            />
        ) : null;

        const showSubtitle = Boolean(settings.showHeaderSubtitle || settings.showHeaderTagline || settings.siteTagline || settings.headerSubtitle);
        const subtitleText = stripHtml(settings.headerSubtitle || settings.headerTagline || settings.siteTagline || '');
        const subtitlePlacement = settings.headerSubtitlePlacement || 'below';

        const subtitleElement = (showSubtitle && subtitleText) ? (
            <span
                className="brand-subtitle-text"
                style={{
                    display: 'block',
                    fontSize: addUnit(settings.headerSubtitleFontSize || '0.75rem'),
                    fontWeight: settings.headerSubtitleFontWeight || 'normal',
                    fontFamily: settings.headerSubtitleFontFamily || 'inherit',
                    fontStyle: settings.headerSubtitleFontStyle || 'normal',
                    color: settings.headerSubtitleColor || 'rgba(255,255,255,0.75)',
                    letterSpacing: addUnit(settings.headerSubtitleLetterSpacing || 'normal'),
                    textTransform: settings.headerSubtitleTextTransform || 'none',
                    margin: addUnit(settings.headerSubtitleMargin || '0px'),
                    opacity: settings.headerSubtitleOpacity !== undefined ? settings.headerSubtitleOpacity : 0.85,
                    lineHeight: 1.2
                }}
            >
                {subtitleText}
            </span>
        ) : null;

        const titleElement = (
            <span className="brand-title-text" style={{ whiteSpace: 'nowrap' }}>
                {stripHtml(settings.siteTitle) || (hasLogo ? '' : 'OMNIVASO')}
            </span>
        );

        const textGroup = showTitle ? (
            subtitleElement ? (
                <div style={{
                    display: 'flex',
                    flexDirection: subtitlePlacement === 'beside' ? 'row' : 'column',
                    alignItems: subtitlePlacement === 'beside' ? 'center' : 'flex-start',
                    gap: subtitlePlacement === 'beside' ? '8px' : '2px',
                    lineHeight: 1.2
                }}>
                    {titleElement}
                    {subtitleElement}
                </div>
            ) : titleElement
        ) : null;

        const brandContent = hasLogo && textGroup ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: addUnit(settings.logoTextGap || '10px') }}>
                {logoImg}
                {textGroup}
            </div>
        ) : (logoImg || textGroup);

        const handleBrandClick = (e) => {
            if (isEditing) {
                e.preventDefault();
                return;
            }
            const isCurrentHome = location.pathname === '/' || location.pathname === (getAppBase() || '') || location.pathname === '';
            if (!brandTarget || brandTarget === '/' || brandTarget === '' || brandTarget.startsWith('#')) {
                if (onAnchorClick) {
                    onAnchorClick(e, brandTarget && brandTarget.startsWith('#') ? brandTarget : '#header');
                } else if (!isCurrentHome) {
                    e.preventDefault();
                    const targetId = brandTarget && brandTarget.startsWith('#') ? brandTarget.replace('#', '') : 'header';
                    navigate('/', { state: { targetId } });
                }
            }
        };

        if (isExternal) {
            return (
                <a
                    href={brandTarget}
                    className="brand-link"
                    style={{
                        ...brandStyle,
                        justifyContent: settings.logoAlignment === 'center' ? 'center' : (settings.logoAlignment === 'right' ? 'flex-end' : 'flex-start')
                    }}
                    onClick={(e) => {
                        if (isEditing) {
                            e.preventDefault();
                        }
                    }}
                >
                    {brandContent}
                </a>
            );
        }

        return (
            <Link
                to={brandTarget || "/"}
                onClick={handleBrandClick}
                className="brand-link"
                style={{
                    ...brandStyle,
                    justifyContent: settings.logoAlignment === 'center' ? 'center' : (settings.logoAlignment === 'right' ? 'flex-end' : 'flex-start')
                }}
            >
                {brandContent}
            </Link>
        );
    };

    const renderSearchButton = (position) => {
        if (!settings.showSearchInHeader) return null;
        if (position === 'left' && settings.headerSearchPosition !== 'left') return null;
        if (position === 'center' && settings.headerSearchPosition !== 'center') return null;
        if (position === 'right' && settings.headerSearchPosition && settings.headerSearchPosition !== 'right') return null;

        if (settings.searchLayout === 'inline') {
            return (
                <div className="header-search-inline-wrap" style={{ margin: position === 'left' ? '0 10px 0 14px' : (position === 'center' ? '0 10px' : '0 0 0 6px') }}>
                    <InlineSearchBar config={config.settings ? config : { settings }} onSetConfig={onSetConfig} onSaveConfig={onSaveConfig} isEditing={isEditing} compact={true} />
                </div>
            );
        }

        return (
            <button
                type="button"
                onClick={(e) => {
                    if (onAnchorClick) onAnchorClick(e, '#search');
                    window.dispatchEvent(new CustomEvent('open-search-tool'));
                }}
                style={{
                    ...linkStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: settings.headerSearchBtnBg || 'rgba(255, 255, 255, 0.1)',
                    color: settings.headerSearchBtnColor || settings.navLinkColor || '#ffffff',
                    fontSize: addUnit(settings.headerSearchBtnFontSize || '0.85rem'),
                    fontWeight: settings.headerSearchBtnFontWeight || 'bold',
                    padding: addUnit(settings.headerSearchBtnPadding || '6px 14px'),
                    borderRadius: addUnit(settings.headerSearchBtnRadius || '20px'),
                    border: settings.headerSearchBtnBorder || '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    margin: position === 'left' ? '0 0 0 14px' : (position === 'center' ? '0 10px' : '0 0 0 6px')
                }}
                onMouseOver={e => e.currentTarget.style.background = settings.primaryColor || 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={e => e.currentTarget.style.background = settings.headerSearchBtnBg || 'rgba(255, 255, 255, 0.1)'}
            >
                {settings.headerSearchButtonText || '🔍 Search ⌘K'}
            </button>
        );
    };

    const renderEcommerceActions = () => {
        if (!isEcommerceEnabled) return null;
        const shopBg = settings.shopButtonTransparent || settings.shopButtonBg === 'transparent' || settings.shopButtonBg === 'none'
            ? 'transparent'
            : (settings.shopButtonBg || 'rgba(255, 255, 255, 0.1)');
        const shopHoverBg = settings.shopButtonHoverTransparent || settings.shopButtonHoverBg === 'transparent' || settings.shopButtonHoverBg === 'none'
            ? 'transparent'
            : (settings.shopButtonHoverBg || settings.shopButtonBg || 'rgba(255, 255, 255, 0.2)');

        const cartBg = settings.cartButtonTransparent || settings.cartButtonBg === 'transparent' || settings.cartButtonBg === 'none'
            ? 'transparent'
            : (settings.cartButtonBg || settings.primaryColor || '#3b82f6');
        const cartHoverBg = settings.cartButtonHoverTransparent || settings.cartButtonHoverBg === 'transparent' || settings.cartButtonHoverBg === 'none'
            ? 'transparent'
            : (settings.cartButtonHoverBg || settings.cartButtonBg || settings.primaryColor || '#2563eb');

        const ordersBg = settings.ordersButtonTransparent || settings.ordersButtonBg === 'transparent' || settings.ordersButtonBg === 'none'
            ? 'transparent'
            : (settings.ordersButtonBg || shopBg || 'rgba(255, 255, 255, 0.1)');
        const ordersHoverBg = settings.ordersButtonHoverTransparent || settings.ordersButtonHoverBg === 'transparent' || settings.ordersButtonHoverBg === 'none'
            ? 'transparent'
            : (settings.ordersButtonHoverBg || settings.ordersButtonBg || shopHoverBg || 'rgba(255, 255, 255, 0.2)');

        return (
            <div className="header-ecommerce-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {!settings.hideHeaderShopButton && (
                    <Link
                        to={normalizeAppUrl('/shop')}
                        onMouseEnter={() => setIsShopHovered(true)}
                        onMouseLeave={() => setIsShopHovered(false)}
                        style={{
                            ...linkStyle,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: isShopHovered ? shopHoverBg : shopBg,
                            color: isShopHovered
                                ? (settings.shopButtonHoverTextColor || settings.shopButtonTextColor || '#ffffff')
                                : (settings.shopButtonTextColor || '#ffffff'),
                            fontSize: addUnit(settings.shopButtonFontSize || '0.85rem'),
                            fontWeight: settings.shopButtonFontWeight || 'bold',
                            padding: addUnit(settings.shopButtonPadding || '6px 14px'),
                            borderRadius: addUnit(settings.shopButtonBorderRadius || '20px'),
                            border: settings.shopButtonBorder || (shopBg !== 'transparent' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'),
                            boxShadow: settings.shopButtonBoxShadow || 'none',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                            marginLeft: '4px'
                        }}
                    >
                        {settings.shopButtonText || '🛍️ Shop'}
                    </Link>
                )}
                {!settings.hideHeaderCartButton && (
                    <Link
                        to={normalizeAppUrl('/cart')}
                        onMouseEnter={() => setIsCartHovered(true)}
                        onMouseLeave={() => setIsCartHovered(false)}
                        style={{
                            ...linkStyle,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: isCartHovered ? cartHoverBg : cartBg,
                            color: isCartHovered
                                ? (settings.cartButtonHoverTextColor || settings.cartButtonTextColor || '#ffffff')
                                : (settings.cartButtonTextColor || '#ffffff'),
                            fontSize: addUnit(settings.cartButtonFontSize || '0.85rem'),
                            fontWeight: settings.cartButtonFontWeight || 'bold',
                            padding: addUnit(settings.cartButtonPadding || '6px 14px'),
                            borderRadius: addUnit(settings.cartButtonBorderRadius || '20px'),
                            border: settings.cartButtonBorder || 'none',
                            boxShadow: settings.cartButtonBoxShadow || '0 2px 6px rgba(0,0,0,0.15)',
                            textDecoration: 'none',
                            transition: 'all 0.2s ease',
                            marginLeft: '4px'
                        }}
                    >
                        {(() => {
                            const customText = settings.cartButtonText;
                            if (customText) {
                                const hasEmoji = /[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}]/u.test(customText) || customText.includes('🛒');
                                return <span>{hasEmoji ? customText : `🛒 ${customText}`}</span>;
                            }
                            return <span>🛒 Cart</span>;
                        })()}
                        <span style={{
                            background: settings.cartBadgeBg || '#ffffff',
                            color: settings.cartBadgeTextColor || (cartBg !== 'transparent' ? cartBg : settings.primaryColor || '#3b82f6'),
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                        }}>
                            {cartCount}
                        </span>
                    </Link>
                )}
                {!settings.hideHeaderOrdersButton && (
                    <button
                        type="button"
                        onClick={() => setIsOrdersModalOpen(true)}
                        onMouseEnter={() => setIsOrdersHovered(true)}
                        onMouseLeave={() => setIsOrdersHovered(false)}
                        style={{
                            ...linkStyle,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: isOrdersHovered ? ordersHoverBg : ordersBg,
                            color: isOrdersHovered
                                ? (settings.ordersButtonHoverTextColor || settings.ordersButtonTextColor || settings.shopButtonTextColor || '#ffffff')
                                : (settings.ordersButtonTextColor || settings.shopButtonTextColor || '#ffffff'),
                            fontSize: addUnit(settings.ordersButtonFontSize || settings.shopButtonFontSize || '0.85rem'),
                            fontWeight: settings.ordersButtonFontWeight || settings.shopButtonFontWeight || 'bold',
                            padding: addUnit(settings.ordersButtonPadding || settings.shopButtonPadding || '6px 14px'),
                            borderRadius: addUnit(settings.ordersButtonBorderRadius || settings.shopButtonBorderRadius || '20px'),
                            border: settings.ordersButtonBorder || (ordersBg !== 'transparent' ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'),
                            boxShadow: settings.ordersButtonBoxShadow || 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            marginLeft: '4px'
                        }}
                    >
                        {settings.ordersButtonText || '📦 My Orders'}
                    </button>
                )}
            </div>
        );
    };

    const renderSocialIcons = () => {
        if (!showHeaderSocials || !(socials || []).length) return null;
        return (
            <div className="header-socials" style={{ display: 'inline-flex', gap: addUnit(settings.headerSocialsGap || '10px'), alignItems: 'center', marginLeft: '6px' }}>
                {socials.map((s, i) => (
                    <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        title={s.platform}
                        style={{
                            color: settings.headerSocialsColor || settings.navLinkColor || 'white',
                            opacity: 0.85,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: addUnit(settings.headerSocialsSize || '28px'),
                            height: addUnit(settings.headerSocialsSize || '28px'),
                            borderRadius: settings.headerSocialsStyle === 'rounded' ? '6px' : settings.headerSocialsStyle === 'minimal' ? '0px' : '50%',
                            background: settings.headerSocialsStyle === 'minimal' ? 'transparent' : (settings.headerSocialsBg || 'rgba(255,255,255,0.1)'),
                            transition: 'all 0.2s ease',
                            textDecoration: 'none'
                        }}
                        onMouseOver={e => {
                            if (settings.primaryColor && settings.headerSocialsStyle !== 'minimal') e.currentTarget.style.background = settings.primaryColor;
                            e.currentTarget.style.opacity = '1';
                        }}
                        onMouseOut={e => {
                            if (settings.headerSocialsStyle !== 'minimal') e.currentTarget.style.background = settings.headerSocialsBg || 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.opacity = '0.85';
                        }}
                    >
                        {renderIcon(s.platform, 14)}
                    </a>
                ))}
            </div>
        );
    };

    const renderNavLinksList = (links) => {
        if (!links || links.length === 0) {
            return (
                <Link to={normalizeAppUrl('/articles')} style={linkStyle}>Articles</Link>
            );
        }
        return links.map((parent, i) => {
            const parentStyle = {
                ...linkStyle,
                ...(parent.transform || parent.textTransform ? { textTransform: parent.transform || parent.textTransform } : {})
            };
            if (parent.children && parent.children.length > 0) {
                return (
                    <div key={i} className="nav-item-container">
                        <span style={{ ...parentStyle, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {parent.text} ▾
                        </span>
                        <div className="dropdown-menu" style={{ background: activeHeaderBg || settings.secondaryColor || '#1e293b' }}>
                            {parent.children.map((child, ci) => {
                                const childStyle = {
                                    ...childLinkStyle,
                                    ...(child.transform || child.textTransform ? { textTransform: child.transform || child.textTransform } : {})
                                };
                                return renderLink(child.url, child.text, ci, childStyle);
                            })}
                        </div>
                    </div>
                );
            }
            return renderLink(parent.url, parent.text, i, parentStyle);
        });
    };

    const isCurrencyExplicitlyDisabled = settings.hideCurrencySelector === true || settings.currencySelectorDisabled === true || settings.hideHeaderCurrencySelector === true;
    const isCurrencyExplicitlyEnabled = settings.currencyEngineEnabled === true || settings.enableMultiCurrency === true || (settings.hideCurrencySelector === false && settings.currencySelectorDisabled === false && settings.hideHeaderCurrencySelector === false);
    const isCurrencyEnabled = !isCurrencyExplicitlyDisabled && (isEcommerceEnabled || isCurrencyExplicitlyEnabled);
    const isLangEnabled = isI18nEnabled({ modules });

    const renderRightActionsNode = () => (
        <div className="nav-actions-cluster" style={{ display: 'inline-flex', alignItems: 'center', gap: actionsGap }}>
            {isCurrencyEnabled && settings.currencySelectorPlacement === 'header-left' && <CurrencySelector settings={settings} />}
            {isLangEnabled && settings.i18nSelectorPlacement === 'header-left' && (
                <LanguageSelector settings={settings} />
            )}
            {renderSearchButton('right')}
            {renderEcommerceActions()}
            {renderSocialIcons()}
            {isCurrencyEnabled && (!settings.currencySelectorPlacement || settings.currencySelectorPlacement === 'header-right' || settings.currencySelectorPlacement === 'nav-inline' || settings.currencySelectorPlacement === 'floating') && (
                <CurrencySelector settings={settings} />
            )}
            {isLangEnabled && (!settings.i18nSelectorPlacement || settings.i18nSelectorPlacement === 'header-right' || settings.i18nSelectorPlacement === 'floating') && (
                <LanguageSelector settings={settings} />
            )}
        </div>
    );

    // Split links for split layout
    const splitIndex = Math.ceil(nestedLinks.length / 2);
    const leftSplitLinks = nestedLinks.slice(0, splitIndex);
    const rightSplitLinks = nestedLinks.slice(splitIndex);

    // ── Floating Navigation Overlay Sub-Renderer ──────────────────────────────
    const renderFloatingNavOverlay = () => {
        if (!isNavFloating) return null;
        const floatZIndex = settings.navFloatZIndex !== undefined ? parseInt(settings.navFloatZIndex, 10) : 10;
        const isDistributed = navAlignment === 'space-between' || navAlignment === 'space-around';
        return (
            <div
                className="site-nav-floating-nav nav-desktop"
                style={{
                    position: 'absolute',
                    top: addUnit(settings.navFloatOffsetY || '0px'),
                    bottom: 0,
                    left: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: navAlignment,
                    gap: navLinkGap,
                    zIndex: floatZIndex,
                    pointerEvents: 'none',
                    paddingLeft: addUnit(settings.navFloatPaddingLeft || '0px'),
                    paddingRight: addUnit(settings.navFloatPaddingRight || '0px'),
                    boxSizing: 'border-box'
                }}
            >
                <div
                    className="site-nav-floating-inner"
                    style={{
                        display: isDistributed ? 'flex' : 'inline-flex',
                        width: isDistributed ? '100%' : 'auto',
                        justifyContent: navAlignment,
                        alignItems: 'center',
                        gap: navLinkGap,
                        pointerEvents: isDistributed ? 'none' : 'auto',
                        background: settings.navFloatBgColor || 'transparent',
                        ...(settings.navFloatBackdropBlur && settings.navFloatBackdropBlur !== '0px' && settings.navFloatBackdropBlur !== '0' ? {
                            backdropFilter: `blur(${addUnit(settings.navFloatBackdropBlur)})`,
                            WebkitBackdropFilter: `blur(${addUnit(settings.navFloatBackdropBlur)})`
                        } : {}),
                        borderRadius: addUnit(settings.navFloatBorderRadius || '0px'),
                        padding: settings.navFloatPadding ? addUnit(settings.navFloatPadding) : '0px',
                        border: settings.navFloatBorder || 'none',
                        boxShadow: settings.navFloatShadow || 'none'
                    }}
                >
                    {renderSearchButton('center')}
                    {renderNavLinksList(nestedLinks)}
                </div>
            </div>
        );
    };

    // ── Header Layout Variations ───────────────────────────────────────────────
    const renderDesktopContent = () => {
        switch (layout) {
            case 'logo-left-nav-center':
                return (
                    <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '16px', position: 'relative' }}>
                        {/* Left Brand */}
                        <div className="site-nav-left-brand" style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '0 0 auto', position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                            {renderBrandNode()}
                            {renderSearchButton('left')}
                        </div>

                        {/* Floating Nav Overlay (if enabled) */}
                        {renderFloatingNavOverlay()}

                        {/* Center Menu (if NOT floating) */}
                        {!isNavFloating && (
                            <div className="site-nav-center-menu nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: navAlignment, flex: '1 1 auto', gap: navLinkGap, position: 'relative', zIndex: 5 }}>
                                {renderNavLinksList(nestedLinks)}
                                {renderSearchButton('center')}
                            </div>
                        )}

                        {/* Right Actions */}
                        <div className="site-nav-right-actions nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 auto', gap: actionsGap, position: 'relative', zIndex: 15 }}>
                            {renderRightActionsNode()}
                        </div>
                    </div>
                );

            case 'split':
                if (isNavFloating) {
                    return (
                        <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px', position: 'relative' }}>
                            <div className="site-nav-split-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', flex: '0 0 auto', position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                                {renderBrandNode()}
                            </div>
                            {renderFloatingNavOverlay()}
                            <div className="site-nav-right-actions nav-desktop" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: actionsGap, zIndex: 15 }}>
                                {renderRightActionsNode()}
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px', position: 'relative' }}>
                        {/* Left Menu Half */}
                        <div className="site-nav-split-left nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '1 1 0', gap: navLinkGap }}>
                            {renderNavLinksList(leftSplitLinks)}
                        </div>
                        {/* Center Brand */}
                        <div className="site-nav-split-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', padding: '0 20px', position: 'relative', zIndex: 2 }}>
                            {renderBrandNode()}
                        </div>
                        {/* Right Menu Half + Actions */}
                        <div className="site-nav-split-right nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flex: '1 1 0', gap: navLinkGap }}>
                            {renderNavLinksList(rightSplitLinks)}
                            {renderRightActionsNode()}
                        </div>
                    </div>
                );

            case 'center': // Logo Center / Menu Below (Stacked Center)
                if (isNavFloating) {
                    return (
                        <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                            <div className="site-nav-stacked-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                                {renderBrandNode()}
                            </div>
                            {renderFloatingNavOverlay()}
                            <div className="site-nav-right-actions nav-desktop" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: actionsGap, zIndex: 15 }}>
                                {renderRightActionsNode()}
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="site-nav-stacked" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '12px', position: 'relative' }}>
                        {/* Top Brand */}
                        <div className="site-nav-stacked-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative', zIndex: 2 }}>
                            {renderBrandNode()}
                        </div>
                        {/* Bottom Menu & Actions */}
                        <div className="site-nav-stacked-nav nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: navAlignment, flexWrap: 'wrap', width: '100%', gap: navLinkGap, position: 'relative', zIndex: 5 }}>
                            {renderSearchButton('left')}
                            {renderNavLinksList(nestedLinks)}
                            {renderSearchButton('center')}
                            {renderRightActionsNode()}
                        </div>
                    </div>
                );

            case 'logo-left-nav-below': // Logo Left / Menu Below (Stacked Left)
                if (isNavFloating) {
                    return (
                        <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '16px', position: 'relative' }}>
                            <div className="site-nav-left-brand" style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '0 0 auto', position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                                {renderBrandNode()}
                                {renderSearchButton('left')}
                            </div>
                            {renderFloatingNavOverlay()}
                            <div className="site-nav-right-actions nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 auto', gap: actionsGap, position: 'relative', zIndex: 15 }}>
                                {renderRightActionsNode()}
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="site-nav-stacked" style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px', position: 'relative' }}>
                        {/* Top Row: Brand on Left, Actions on Right */}
                        <div className="site-nav-stacked-top-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', position: 'relative', zIndex: 2 }}>
                                {renderBrandNode()}
                                {renderSearchButton('left')}
                            </div>
                            <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: actionsGap, position: 'relative', zIndex: 15 }}>
                                {renderRightActionsNode()}
                            </div>
                        </div>
                        {/* Bottom Row: Navigation Menu */}
                        <div className="site-nav-stacked-bottom-row nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: navAlignment, width: '100%', gap: navLinkGap, paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', position: 'relative', zIndex: 5 }}>
                            {renderNavLinksList(nestedLinks)}
                            {renderSearchButton('center')}
                        </div>
                    </div>
                );

            case 'right': // Logo Right / Menu Left
                return (
                    <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '16px', position: 'relative' }}>
                        {/* Nav Links & Actions on Left */}
                        <div className="site-nav-right-actions nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flex: '0 0 auto', gap: actionsGap, order: 1, position: 'relative', zIndex: 15 }}>
                            {renderRightActionsNode()}
                        </div>

                        {/* Floating Nav Overlay (if enabled) */}
                        {renderFloatingNavOverlay()}

                        {/* Nav Links (if NOT floating) */}
                        {!isNavFloating && (
                            <div
                                className="site-nav-menu-container nav-desktop"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: navAlignment,
                                    flex: '1 1 auto',
                                    gap: navLinkGap,
                                    order: 2,
                                    position: 'relative',
                                    zIndex: 5
                                }}
                            >
                                {renderSearchButton('left')}
                                {renderNavLinksList(nestedLinks)}
                                {renderSearchButton('center')}
                            </div>
                        )}

                        {/* Brand on Right */}
                        <div className="site-nav-right-brand" style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '0 0 auto', order: 3, position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                            {renderBrandNode()}
                        </div>
                    </div>
                );

            case 'left': // Classic Logo Left / Menu Right (Default)
            default:
                return (
                    <div className="site-nav-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '16px', position: 'relative' }}>
                        {/* Left: Brand */}
                        <div className="site-nav-left-brand" style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: '0 0 auto', position: 'relative', zIndex: (settings.navFloatZIndex !== undefined && parseInt(settings.navFloatZIndex, 10) < 5) ? 5 : 2 }}>
                            {renderBrandNode()}
                            {renderSearchButton('left')}
                        </div>

                        {/* Floating Nav Overlay (if enabled) */}
                        {renderFloatingNavOverlay()}

                        {/* In-Flow Desktop Nav Links (if NOT floating) */}
                        {!isNavFloating && (
                            <div
                                className="site-nav-menu-container nav-desktop"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: navAlignment,
                                    flex: '1 1 auto',
                                    gap: navLinkGap,
                                    position: 'relative',
                                    zIndex: 5
                                }}
                            >
                                {renderSearchButton('center')}
                                {renderNavLinksList(nestedLinks)}
                            </div>
                        )}

                        {/* Right: Actions */}
                        <div className="site-nav-right-actions nav-desktop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 auto', gap: actionsGap, position: 'relative', zIndex: 15 }}>
                            {renderRightActionsNode()}
                        </div>
                    </div>
                );
        }
    };

    return (
        <nav className={`site-navigation ${forceMobile ? 'force-mobile' : ''}`} style={navStyle}>
            <style>{`
                .site-nav-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: relative;
                    width: ${settings.headerContainerWidth ? addUnit(settings.headerContainerWidth) : '100%'};
                    max-width: ${(() => {
                    if (settings.headerWidthMode === 'full') return '100%';
                    if (settings.headerWidthMode === 'wide') return '1400px';
                    if (settings.headerWidthMode === 'compact') return '960px';
                    if (settings.headerMaxWidth) {
                        const val = String(settings.headerMaxWidth).trim().toLowerCase();
                        if (['100%', 'none', 'full'].includes(val)) return '100%';
                        return addUnit(settings.headerMaxWidth);
                    }
                    return addUnit(settings.containerMaxWidth || '1100px');
                })()};
                    box-sizing: border-box;
                    padding-top: var(--nav-pad-t, 0px);
                    padding-bottom: var(--nav-pad-b, 0px);
                    padding-left: var(--nav-pad-l, 20px);
                    padding-right: var(--nav-pad-r, 20px);
                }
                .site-nav-container.layout-right {
                    flex-direction: row-reverse;
                }
                .site-nav-row {
                    position: relative;
                    width: 100%;
                }
                .nav-item-container {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                    pointer-events: auto;
                }
                @media (min-width: 992px) {
                    .site-nav-container.layout-center:not(.nav-floating) {
                        flex-direction: column;
                        justify-content: center;
                        gap: 12px;
                    }
                    .site-nav-container.layout-logo-left-nav-below:not(.nav-floating) {
                        flex-direction: column;
                        justify-content: center;
                        gap: 8px;
                    }
                }
                @media (max-width: 991px) {
                    .site-navigation {
                        ${settings.mobileHeaderBg ? `background: ${settings.mobileHeaderBg} !important;` : ''}
                        padding-top: ${addUnit(settings.mobileHeaderPaddingTopBottom || settings.headerPaddingTopBottom || '12px')} !important;
                        padding-bottom: ${addUnit(settings.mobileHeaderPaddingTopBottom || settings.headerPaddingTopBottom || '12px')} !important;
                    }
                    .site-nav-container {
                        display: flex !important;
                        flex-direction: ${settings.mobileHamburgerPosition === 'left' ? 'row-reverse' : 'row'} !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                        padding-top: ${addUnit(settings.mobileHeaderPaddingTop || '0px')} !important;
                        padding-bottom: ${addUnit(settings.mobileHeaderPaddingBottom || '0px')} !important;
                        padding-left: ${addUnit(settings.mobileHeaderPaddingLeft || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingLeft || settings.headerPaddingLeftRight || '16px')} !important;
                        padding-right: ${addUnit(settings.mobileHeaderPaddingRight || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingRight || settings.headerPaddingLeftRight || '16px')} !important;
                    }
                    .site-nav-container.mobile-logo-center {
                        position: relative;
                        justify-content: center !important;
                    }
                    .site-nav-container.mobile-logo-center .brand-link {
                        margin: 0 auto !important;
                    }
                    .site-nav-container.mobile-logo-center .nav-mobile-trigger {
                        position: absolute;
                        right: ${settings.mobileHamburgerPosition === 'left' ? 'auto' : addUnit(settings.mobileHeaderPaddingRight || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingRight || settings.headerPaddingLeftRight || '16px')};
                        left: ${settings.mobileHamburgerPosition === 'left' ? addUnit(settings.mobileHeaderPaddingLeft || settings.mobileHeaderPaddingLeftRight || settings.headerPaddingLeft || settings.headerPaddingLeftRight || '16px') : 'auto'};
                        top: 50%;
                        transform: translateY(-50%);
                    }
                    .site-nav-row, .site-nav-stacked, .site-nav-stacked-top-row {
                        display: flex !important;
                        flex-direction: row !important;
                        justify-content: ${mobileLogoCenter ? 'center' : 'space-between'} !important;
                        align-items: center !important;
                        width: 100% !important;
                    }
                    .brand-link img {
                        max-height: ${addUnit(settings.mobileLogoMaxHeight || settings.mobileLogoHeight || settings.siteLogoHeight || '36px')} !important;
                        max-width: ${addUnit(settings.mobileLogoMaxWidth || settings.mobileLogoWidth || '160px')} !important;
                        width: auto !important;
                        object-fit: contain !important;
                    }
                }
            `}</style>
            <div
                className={`container site-nav-container layout-${layout} ${mobileLogoCenter ? 'mobile-logo-center' : ''} ${isNavFloating ? 'nav-floating' : ''}`}
                style={{
                    '--nav-pad-t': addUnit(settings.headerPaddingTop || settings.headerContainerPaddingTop || '0px'),
                    '--nav-pad-b': addUnit(settings.headerPaddingBottom || settings.headerContainerPaddingBottom || '0px'),
                    '--nav-pad-l': addUnit(settings.headerPaddingLeft || settings.headerPaddingLeftRight || '20px'),
                    '--nav-pad-r': addUnit(settings.headerPaddingRight || settings.headerPaddingLeftRight || '20px')
                }}
            >
                {renderDesktopContent()}

                {forceMobile && (
                    <style>{`
                        .site-navigation.force-mobile .nav-desktop { display: none !important; }
                        .site-navigation.force-mobile .nav-mobile-trigger { display: inline-flex !important; }
                        .site-navigation.force-mobile .mobile-menu { display: flex !important; }
                        .site-navigation.force-mobile .mobile-overlay { display: block !important; }
                        .site-navigation.force-mobile .site-nav-container {
                            display: flex !important;
                            flex-direction: ${settings.mobileHamburgerPosition === 'left' ? 'row-reverse' : 'row'} !important;
                            justify-content: ${mobileLogoCenter ? 'center' : 'space-between'} !important;
                            align-items: center !important;
                            width: 100% !important;
                        }
                        ${mobileLogoCenter ? `
                            .site-navigation.force-mobile .site-nav-container .brand-link { margin: 0 auto !important; }
                            .site-navigation.force-mobile .site-nav-container .nav-mobile-trigger {
                                position: absolute;
                                right: ${settings.mobileHamburgerPosition === 'left' ? 'auto' : '16px'};
                                left: ${settings.mobileHamburgerPosition === 'left' ? '16px' : 'auto'};
                                top: 50%;
                                transform: translateY(-50%);
                            }
                        ` : ''}
                    `}</style>
                )}

                {/* Mobile Hamburger Trigger */}
                <button
                    type="button"
                    className={`nav-mobile-trigger ${isMenuOpen ? 'open' : ''} ${forceMobile ? 'force-mobile-trigger' : ''}`}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    aria-label="Toggle mobile navigation menu"
                    aria-expanded={isMenuOpen}
                    style={{
                        display: forceMobile ? 'inline-flex' : undefined,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        background: settings.mobileHamburgerBg || (settings.mobileHamburgerIconStyle === 'pill-capsule' ? 'rgba(255,255,255,0.1)' : 'transparent'),
                        border: settings.mobileHamburgerBorder || (settings.mobileHamburgerIconStyle === 'pill-capsule' ? '1px solid rgba(255,255,255,0.2)' : 'none'),
                        borderRadius: addUnit(settings.mobileHamburgerRadius || (settings.mobileHamburgerIconStyle === 'circle-badge' ? '50%' : settings.mobileHamburgerIconStyle === 'pill-capsule' ? '20px' : '8px')),
                        padding: addUnit(settings.mobileHamburgerPadding || (settings.mobileHamburgerIconStyle === 'pill-capsule' ? '6px 14px' : settings.mobileHamburgerIconStyle === 'circle-badge' ? '0px' : '6px 8px')),
                        width: settings.mobileHamburgerIconStyle === 'circle-badge' ? '38px' : 'auto',
                        height: settings.mobileHamburgerIconStyle === 'circle-badge' ? '38px' : 'auto',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        touchAction: 'manipulation',
                        outline: 'none'
                    }}
                >
                    {settings.mobileHamburgerIconStyle === 'bars-minimal' ? (
                        /* Option 1: Dual-Bar Minimalist */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '20px' }}>
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block', transition: 'all 0.25s ease' }} />
                            <span style={{ width: '65%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block', transition: 'all 0.25s ease' }} />
                        </div>
                    ) : settings.mobileHamburgerIconStyle === 'morph-cross' ? (
                        /* Option 2: Morphing Cross */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '20px', height: '16px', justifyContent: 'center' }}>
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block', transition: 'transform 0.25s ease', transform: isMenuOpen ? 'rotate(45deg) translateY(5px)' : 'none' }} />
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block', transition: 'opacity 0.2s ease', opacity: isMenuOpen ? 0 : 1 }} />
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block', transition: 'transform 0.25s ease', transform: isMenuOpen ? 'rotate(-45deg) translateY(-5px)' : 'none' }} />
                        </div>
                    ) : settings.mobileHamburgerIconStyle === 'pill-capsule' || settings.mobileHamburgerIconStyle === 'badge' ? (
                        /* Option 3: Floating Capsule Pill */
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '16px' }}>
                                <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                                <span style={{ width: '60%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: hamburgerBarColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {settings.mobileHamburgerText || 'MENU'}
                            </span>
                        </div>
                    ) : settings.mobileHamburgerIconStyle === 'dots-grid' || settings.mobileHamburgerIconStyle === 'dots' ? (
                        /* Option 4: 3-Dot Grid Matrix */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', padding: '2px' }}>
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: hamburgerBarColor, display: 'block' }} />
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: hamburgerBarColor, display: 'block' }} />
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: hamburgerBarColor, display: 'block' }} />
                        </div>
                    ) : settings.mobileHamburgerIconStyle === 'circle-badge' ? (
                        /* Option 5: Circle Badge Icon */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '18px', alignItems: 'center' }}>
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                            <span style={{ width: '70%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                        </div>
                    ) : (
                        /* Classic 3 Bars default */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '22px' }}>
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                            <span style={{ width: '100%', height: '2px', background: hamburgerBarColor, borderRadius: '2px', display: 'block' }} />
                        </div>
                    )}
                </button>
            </div>

            {/* Injected Custom Mobile Menu & Hamburger CSS */}
            {Boolean(settings.mobileMenuCustomCss || settings.mobileHamburgerCustomCss) && (
                <style dangerouslySetInnerHTML={{
                    __html: `
                        ${settings.mobileHamburgerCustomCss || ''}
                        ${settings.mobileMenuCustomCss || ''}
                    `
                }} />
            )}

            {/* Mobile Overlay */}
            <div
                id="site-mobile-overlay"
                className={`mobile-overlay ${isMenuOpen ? 'open' : ''} ${forceMobile ? 'force-mobile-overlay' : ''}`}
                onClick={() => setIsMenuOpen(false)}
                style={{
                    position: forceMobile ? 'absolute' : 'fixed',
                    ...(settings.mobileMenuBackdropBg ? { background: settings.mobileMenuBackdropBg } : {}),
                    ...(settings.mobileMenuBackdropBlur ? {
                        backdropFilter: `blur(${addUnit(settings.mobileMenuBackdropBlur)})`,
                        WebkitBackdropFilter: `blur(${addUnit(settings.mobileMenuBackdropBlur)})`
                    } : {})
                }}
            />

            {/* Mobile Drawer */}
            <div
                id="site-mobile-menu"
                className={`mobile-menu layout-${layoutMode} ${isMenuOpen ? 'open' : ''} ${forceMobile ? 'force-mobile-menu' : ''}`}
                style={mobileDrawerStyle}
            >
                {/* Bottom Sheet Drag Pill Handle */}
                {isBottomSheet && (
                    <div
                        className="mobile-sheet-drag-handle"
                        style={{
                            width: '44px',
                            height: '5px',
                            background: 'rgba(255, 255, 255, 0.35)',
                            borderRadius: '3px',
                            margin: '0 auto 8px auto',
                            flexShrink: 0
                        }}
                    />
                )}

                {/* Mobile Drawer Header Bar */}
                <div
                    className="mobile-menu-header"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        minHeight: '40px',
                        width: '100%',
                        position: 'relative'
                    }}
                >
                    {settings.mobileDrawerShowBrand !== false ? (
                        <div className="mobile-menu-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {settings.mobileDrawerHeaderTitle ? (
                                <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: mobileMenuTextColor }}>
                                    {settings.mobileDrawerHeaderTitle}
                                </span>
                            ) : (
                                <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: mobileMenuTextColor, opacity: 0.9 }}>
                                    {stripHtml(settings.siteTitle || 'Menu')}
                                </span>
                            )}
                        </div>
                    ) : <div />}

                    {/* Close Button */}
                    <button
                        className="mobile-close-btn"
                        onClick={() => setIsMenuOpen(false)}
                        aria-label="Close menu"
                        style={{
                            background: settings.mobileCloseBtnBg || 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: settings.mobileCloseBtnRadius || (settings.mobileCloseBtnStyle === 'square' ? '8px' : settings.mobileCloseBtnStyle === 'pill' ? '20px' : '50%'),
                            width: settings.mobileCloseBtnStyle === 'pill' ? 'auto' : '36px',
                            height: '36px',
                            padding: settings.mobileCloseBtnStyle === 'pill' ? '0 12px' : '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: settings.mobileCloseBtnStyle === 'pill' ? '0.75rem' : '1.2rem',
                            fontWeight: 'bold',
                            color: settings.mobileCloseBtnColor || mobileMenuTextColor,
                            lineHeight: 1,
                            flexShrink: 0,
                            transition: 'all 0.2s ease',
                            zIndex: 1
                        }}
                    >
                        {settings.mobileCloseBtnStyle === 'pill' ? 'CLOSE ✕' : '✕'}
                    </button>
                </div>

                {/* Mobile Navigation Links */}
                <div className="mobile-menu-nav" style={{ display: 'flex', flexDirection: 'column', gap: isGridMode ? '8px' : '12px' }}>
                    {nestedLinks.map((parent, i) => {
                        const closeMenu = () => setIsMenuOpen(false);
                        const closeAndAnchor = (e) => { closeMenu(); if (onAnchorClick) onAnchorClick(e, parent.url); };
                        const hasChildren = parent.children && parent.children.length > 0;
                        const isAccordionMode = settings.mobileSubmenuMode !== 'expanded';
                        const isSubmenuOpen = !isAccordionMode || !!mobileExpandedMenus[i];

                        if (hasChildren) {
                            return (
                                <div key={i} className="mobile-nav-item mobile-nav-parent" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div
                                        className="mobile-nav-parent-header"
                                        onClick={(e) => {
                                            if (isAccordionMode) {
                                                toggleMobileSubmenu(i, e);
                                            } else {
                                                if (parent.url && parent.url !== '#') {
                                                    if (parent.url.startsWith('#')) closeAndAnchor(e);
                                                    else closeMenu();
                                                }
                                            }
                                        }}
                                        style={{
                                            ...mobileLinkStyle,
                                            ...(parent.transform || parent.textTransform ? { textTransform: parent.transform || parent.textTransform } : {}),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <span className="mobile-nav-parent-text">{parent.text}</span>
                                        {isAccordionMode && (
                                            <span
                                                className="mobile-submenu-toggle"
                                                style={{
                                                    fontSize: '0.75rem',
                                                    opacity: 0.7,
                                                    transition: 'transform 0.25s ease',
                                                    transform: isSubmenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                                                }}
                                            >
                                                ▼
                                            </span>
                                        )}
                                    </div>
                                    {isSubmenuOpen && (
                                        <div
                                            className="mobile-submenu-container"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px',
                                                paddingLeft: isGridMode ? '0px' : '14px',
                                                borderLeft: isGridMode ? 'none' : '2px solid rgba(255,255,255,0.15)',
                                                marginTop: '2px',
                                                marginBottom: '4px'
                                            }}
                                        >
                                            {parent.children.map((child, ci) => {
                                                const childClose = () => setIsMenuOpen(false);
                                                const childCloseAnchor = (e) => { childClose(); if (onAnchorClick) onAnchorClick(e, child.url); };
                                                const mobileChildStyle = {
                                                    ...mobileChildLinkStyle,
                                                    ...(child.transform || child.textTransform ? { textTransform: child.transform || child.textTransform } : {}),
                                                    className: 'mobile-child-link'
                                                };
                                                return (
                                                    <div key={ci} className="mobile-child-link-wrapper">
                                                        {renderLink(
                                                            child.url,
                                                            child.text,
                                                            ci,
                                                            mobileChildStyle,
                                                            child.url.startsWith('#') ? childCloseAnchor : childClose
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const mobileItemStyle = {
                            ...mobileLinkStyle,
                            ...(parent.transform || parent.textTransform ? { textTransform: parent.transform || parent.textTransform } : {}),
                            className: 'mobile-nav-link'
                        };
                        return (
                            <div key={i} className="mobile-nav-item">
                                {renderLink(
                                    parent.url,
                                    parent.text,
                                    i,
                                    mobileItemStyle,
                                    parent.url.startsWith('#') ? closeAndAnchor : closeMenu
                                )}
                            </div>
                        );
                    })}
                    {(!settings.navLinks || settings.navLinks.length === 0) && (
                        <Link to={normalizeAppUrl('/articles')} onClick={() => setIsMenuOpen(false)} className="mobile-nav-link" style={mobileLinkStyle}>
                            Articles
                        </Link>
                    )}
                </div>

                {/* Mobile Drawer Action Buttons */}
                <div className="mobile-menu-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                    {Boolean(settings.mobileDrawerShowSearch !== false && settings.showSearchInHeader) && (
                        <button
                            type="button"
                            className="mobile-menu-search-btn"
                            onClick={() => {
                                setIsMenuOpen(false);
                                window.dispatchEvent(new CustomEvent('open-search-tool'));
                            }}
                            style={{
                                ...mobileLinkStyle,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: settings.headerSearchBtnBg || 'rgba(255,255,255,0.09)',
                                color: settings.headerSearchBtnColor || mobileMenuTextColor,
                                padding: '10px 14px',
                                borderRadius: '10px',
                                borderBottom: 'none',
                                border: '1px solid rgba(255,255,255,0.18)',
                                cursor: 'pointer',
                                textAlign: 'left'
                            }}
                        >
                            <span>{settings.headerSearchButtonText || '🔍 Search Site'}</span>
                            <span style={{ fontSize: '0.82rem', opacity: 0.75, padding: '2px 6px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px' }}>⌘K</span>
                        </button>
                    )}

                    {isEcommerceEnabled && (
                        <>
                            {!settings.hideHeaderShopButton && settings.mobileDrawerShowShop !== false && (
                                <Link
                                    to={normalizeAppUrl('/shop')}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="mobile-menu-shop-btn"
                                    style={{
                                        ...mobileLinkStyle,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: settings.shopButtonBg || 'rgba(255,255,255,0.09)',
                                        color: settings.shopButtonTextColor || mobileMenuTextColor,
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        borderBottom: 'none',
                                        border: '1px solid rgba(255,255,255,0.18)'
                                    }}
                                >
                                    <span>{settings.shopButtonText || '🛍️ Online Store'}</span>
                                    <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>Browse →</span>
                                </Link>
                            )}
                            {!settings.hideHeaderCartButton && settings.mobileDrawerShowCart !== false && (
                                <Link
                                    to={normalizeAppUrl('/cart')}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="mobile-menu-cart-btn"
                                    style={{
                                        ...mobileLinkStyle,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: settings.cartButtonBg || 'rgba(255,255,255,0.09)',
                                        color: settings.cartButtonTextColor || mobileMenuTextColor,
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        borderBottom: 'none',
                                        border: '1px solid rgba(255,255,255,0.18)'
                                    }}
                                >
                                    <span>{(() => {
                                        const customText = settings.cartButtonText;
                                        if (customText) {
                                            const hasEmoji = /[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}]/u.test(customText) || customText.includes('🛒');
                                            return hasEmoji ? customText : `🛒 ${customText}`;
                                        }
                                        return '🛒 Shopping Cart';
                                    })()}</span>
                                    <span style={{
                                        background: settings.primaryColor || '#3b82f6',
                                        color: 'white',
                                        borderRadius: '12px',
                                        padding: '2px 10px',
                                        fontSize: '0.85rem',
                                        fontWeight: 'bold'
                                    }}>
                                        {cartCount} {cartCount === 1 ? 'item' : 'items'}
                                    </span>
                                </Link>
                            )}
                            {!settings.hideHeaderOrdersButton && settings.mobileDrawerShowOrders !== false && (
                                <button
                                    type="button"
                                    className="mobile-menu-orders-btn"
                                    onClick={() => { setIsMenuOpen(false); setIsOrdersModalOpen(true); }}
                                    style={{
                                        ...mobileLinkStyle,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: settings.shopButtonBg || 'rgba(255,255,255,0.09)',
                                        color: settings.shopButtonTextColor || mobileMenuTextColor,
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        borderBottom: 'none',
                                        border: '1px solid rgba(255,255,255,0.18)',
                                        cursor: 'pointer',
                                        width: '100%'
                                    }}
                                >
                                    <span>{settings.ordersButtonText || '📦 My Orders'}</span>
                                    <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>History →</span>
                                </button>
                            )}
                        </>
                    )}
                </div>

                {isCurrencyEnabled && (
                    <div className="mobile-currency-wrapper" style={{ marginTop: '8px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.85rem', opacity: 0.8, color: mobileMenuTextColor }}>Currency:</span>
                        <CurrencySelector settings={settings} />
                    </div>
                )}

                {/* Mobile Drawer Socials */}
                {showMobileSocials && (socials || []).length > 0 && (
                    <div className="mobile-menu-socials" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px', paddingTop: '12px', borderTop: dividerBorder }}>
                        {socials.map((s, i) => (
                            <a
                                key={i}
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="mobile-social-icon"
                                aria-label={s.platform || 'Social Link'}
                                style={{
                                    color: mobileMenuTextColor,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {renderIcon(s.platform, 16)}
                            </a>
                        ))}
                    </div>
                )}

            </div>

            {/* Customer Orders / Reorder Modal */}
            <CustomerOrdersModal
                isOpen={isOrdersModalOpen}
                onClose={() => setIsOrdersModalOpen(false)}
                onNavigateToCart={() => navigate(normalizeAppUrl('/cart'))}
                config={config}
            />
        </nav>
    );
};

export default SiteNav;
